import React, { useLayoutEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SecretAuditLog from "components/settings/SecretAuditLog";
import SecretDialog, { MONOSPACE } from "components/settings/SecretDialog";
import { User } from "features/auth/auth";
import { MANAGE_SECRETS_PERMISSION } from "features/auth/permissions";
import {
  Secret,
  UserRef,
  useListSecretsQuery,
} from "features/settings/secrets";
import { useAuth } from "features/store";

const CLAMPED_SX = {
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

// The table keeps a readable minimum width; a narrower screen scrolls inside
// the container, never the page.
const TABLE_MIN_WIDTH = 920;

/** The dialog's state: closed, creating, or editing one secret. */
type DialogTarget = { secret: Secret | null } | null;

/** A description clamped to three lines, with a toggle once it overflows. */
const Description = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || expanded) {
      return undefined;
    }
    const measure = () => setOverflows(box.scrollHeight > box.clientHeight);
    measure();
    // The column narrows and widens with the window.
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [text, expanded]);

  if (!text) {
    return (
      <Typography component="span" variant="body2" color="text.disabled">
        No description
      </Typography>
    );
  }
  return (
    <>
      <Box
        ref={boxRef}
        sx={{
          overflowWrap: "anywhere",
          whiteSpace: "pre-line",
          ...(expanded ? {} : CLAMPED_SX),
        }}
      >
        {text}
      </Box>
      {(overflows || expanded) && (
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setExpanded((shown) => !shown)}
        >
          {expanded ? "Show less" : "Show more"}
        </Link>
      )}
    </>
  );
};

const Stamp = ({ on, by }: { on: string | null; by: UserRef }) => {
  if (!on) {
    return (
      <Typography component="span" variant="body2" color="text.disabled">
        Never
      </Typography>
    );
  }
  return (
    <>
      <time dateTime={on}>{new Date(on).toLocaleString()}</time>
      <Typography variant="body2" color="text.secondary">
        {by ? by.display_name : "Unknown user"}
      </Typography>
    </>
  );
};

const Value = ({ secret }: { secret: Secret }) => {
  if (secret.requires_encryption) {
    return (
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        color="text.secondary"
        whiteSpace="nowrap"
      >
        <LockOutlinedIcon fontSize="small" />
        <Typography component="span" variant="body2">
          Encrypted — hidden
        </Typography>
      </Box>
    );
  }
  return (
    <Box
      component="code"
      fontFamily={MONOSPACE}
      fontSize={13}
      sx={{ overflowWrap: "anywhere" }}
    >
      {secret.value}
    </Box>
  );
};

const Secrets = () => {
  const [dialog, setDialog] = useState<DialogTarget>(null);
  // AuthState.user is still `any`; SettingsPage states the shape the same way.
  const user: User | null | undefined = useAuth().user;
  // The API refuses a write without it; the actions are hidden to match.
  const canManage = (user?.permissions ?? []).includes(
    MANAGE_SECRETS_PERMISSION,
  );
  const { data, isLoading, isError } = useListSecretsQuery();
  const secrets = data ?? [];

  const newSecretButton = (
    <Button
      variant="contained"
      size="small"
      startIcon={<AddIcon />}
      onClick={() => setDialog({ secret: null })}
    >
      New secret
    </Button>
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress aria-label="Loading secrets" />
        </Box>
      );
    }
    if (isError) {
      return <Alert severity="error">Failed to load secrets.</Alert>;
    }
    if (secrets.length === 0) {
      return (
        <Box py={6} textAlign="center">
          <Typography variant="body1" mb={1}>
            No secrets yet.
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            A secret holds a credential or setting the backend reads, such as a
            webhook URL or an API token. Sensitive values are encrypted in your
            browser and never shown again.
          </Typography>
          {canManage && newSecretButton}
        </Box>
      );
    }
    return (
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table
          size="small"
          aria-label="Secrets"
          sx={{ minWidth: TABLE_MIN_WIDTH, "& td": { verticalAlign: "top" } }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "22%", minWidth: 180 }}>Name</TableCell>
              <TableCell sx={{ width: "28%", minWidth: 200 }}>
                Description
              </TableCell>
              <TableCell>Requires encryption</TableCell>
              <TableCell sx={{ width: "18%", minWidth: 150 }}>Value</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Created</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Modified</TableCell>
              {canManage && <TableCell align="right" aria-label="Actions" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {secrets.map((secret) => (
              <TableRow key={secret.id} hover>
                <TableCell>
                  <Box
                    component="code"
                    fontFamily={MONOSPACE}
                    fontSize={13}
                    fontWeight={600}
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    {secret.name}
                  </Box>
                </TableCell>
                <TableCell>
                  <Description text={secret.description} />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={secret.requires_encryption ? "Yes" : "No"}
                    color={secret.requires_encryption ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell>
                  <Value secret={secret} />
                </TableCell>
                <TableCell>
                  <Stamp on={secret.created_on} by={secret.created_by} />
                </TableCell>
                <TableCell>
                  <Stamp on={secret.updated_on} by={secret.updated_by} />
                </TableCell>
                {canManage && (
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        aria-label={`Edit ${secret.name}`}
                        onClick={() => setDialog({ secret })}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        mb={1}
      >
        <Typography variant="body2" color="text.secondary">
          Credentials and settings the backend reads. Encrypted values are never
          shown.
        </Typography>
        {canManage && secrets.length > 0 && newSecretButton}
      </Box>
      {renderBody()}
      <SecretAuditLog />
      {/* Unmounted on close, so a typed value does not outlive the dialog. */}
      {dialog && (
        <SecretDialog secret={dialog.secret} onClose={() => setDialog(null)} />
      )}
    </>
  );
};

export default Secrets;
